import { useEffect, useRef, useState, useMemo } from "react";
import {
  Modal, Box, Typography, FormControl, TextField,
  Button, Grid, CircularProgress, Link,
  IconButton, Table, TableBody, TableCell,
  TableContainer, TableHead, TableRow, Paper,
  Fade, Tooltip, Alert, Chip, Checkbox, FormControlLabel, FormGroup,
  FormHelperText
} from "@mui/material";
import { useSelector } from "react-redux";
import moment from "moment";
import currency from "currency.js";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import LockIcon from '@mui/icons-material/Lock';
import DownloadIcon from '@mui/icons-material/Download';
import DeleteIcon from '@mui/icons-material/Delete';
import CloseIcon from '@mui/icons-material/Close';
import HealthAndSafetyIcon from '@mui/icons-material/HealthAndSafety';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import VerifiedIcon from '@mui/icons-material/Verified';
import AssignmentReturnIcon from '@mui/icons-material/AssignmentReturn';
import GradingIcon from '@mui/icons-material/Grading';
import UndoIcon from '@mui/icons-material/Undo';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';

import {
  ARL, PERMISSIONS, REVIEW_STATUS, REVIEW_DISPLAY_SUBMITTED, REVIEW_DISPLAY_SS_REVIEWED,
  SS_RETURN_FIELDS
} from "../../constants";
import {
  debugLog, getSocialSecurityStatus, getSocialSecurityDisplayStatus,
  getActiveSSReturn, getSSPendingCorrectionFields, isExecutablePeriod,
  getPeriodDateInfo, collectPeriodNews, getSocialSecurityLockIdx, getPeriodNumber,
  getPeriodTaxValues
} from "../../functions";
import { UploadFile } from "../../services/backend/UploadFileService";
import { completionsCreate } from "../../services/backend/OpenAIServices";
import { getUploadDocumentText } from "../../services/backend/ContractExtractionService";
import { updateContract } from "../../services/backend/ContractService";
import { assertPdfNotEncrypted } from "../../services/pdf/validatePdf";
import MoneyInput from "../generic/MoneyInput";
import MySnackbar from "../generic/MySnackbar";
import ReviewStatusChip from "../generic/ReviewStatusChip";
import DialogConfirmation from "../generic/DialogConfirmation";

const modalStyle = {
  bgcolor: "background.paper",
  boxShadow: 24,
  left: "50%",
  maxWidth: "980px",
  maxHeight: "90vh",
  overflowY: "auto",
  p: 4,
  position: "absolute",
  top: "50%",
  transform: "translate(-50%, -50%)",
  width: "90%"
};

const certs = {
  form: 'planilla de seguridad social',
  payroll: 'pago de planilla',
  transaction: 'transacción del pago',
  pensioner: 'resolución de pensionado'
};

// Solo la planilla (form) es obligatoria; el resto son anexos opcionales.
const OPTIONAL_CERTS = ['payroll', 'transaction', 'pensioner'];

// Ayudas por tipo de comprobante: qué documento es y en qué se diferencia
// de los demás (redacción a validar con el equipo funcional en dev).
const CERT_HELP = {
  form: 'Planilla PILA del mes con el detalle de los aportes a salud, pensión y ARL. Es el comprobante obligatorio para la revisión de la seguridad social.',
  payroll: 'Constancia de pago de la planilla emitida por el operador de aportes. No reemplaza la planilla: es el soporte de que fue pagada.',
  transaction: 'Soporte bancario de la transferencia o pago electrónico con que se pagó la planilla. Lo emite el banco, no el operador.',
  pensioner: 'Acto administrativo que acredita la condición de pensionado. Cárguelo solo si el contratista es pensionado.',
};

const SocialSecurityModal = ({
  contract, open, setContract, onClose, getContractFieldWithNews,
  payment: initialPayment
}) => {
  const user = useSelector((state) => state.user.value);


  const [contribution, setContribution] = useState();
  const [payment, setPayment] = useState();
  const [payroll, setPayroll] = useState('');
  const [payAt, setPayAt] = useState(null);
  const [certificates, setCertificates] = useState({ payroll: null, form: null, transaction: null, pensioner: null });
  const [loading, setLoading] = useState(false);
  const [analyzing, setAnalyzing] = useState(false);
  const [infoSnack, setInfoSnack] = useState({ open: false });
  const [showScrollHint, setShowScrollHint] = useState(false);
  const [returnInfo, setReturnInfo] = useState({ open: false });
  const [returnNote, setReturnNote] = useState('');
  const [returnFields, setReturnFields] = useState({});
  const [correctionInfo, setCorrectionInfo] = useState({ open: false });
  const modalRef = useRef(null);

  const updateScrollHint = () => {
    const el = modalRef.current;
    if (!el) return;
    const hasOverflow = el.scrollHeight > el.clientHeight + 1;
    const atBottom = el.scrollHeight - el.scrollTop - el.clientHeight < 8;
    setShowScrollHint(hasOverflow && !atBottom);
  };

  const isContractor = useMemo(() => contract?.contractor?.userId === user.userId, [contract, user]);

  const supportIDs = contract?.supportIDs || [];

  // Supervisor EFECTIVO (con novedades, no el campo estático supervisorIDs) o
  // ADMIN: marca la revisión de la SS con el mismo toggle del Apoyo. No existe
  // una aprobación separada: `Revisada` es el estado terminal del componente.
  const isSupervisorReviewer = useMemo(() => {
    if (user.roles.includes('ADMIN')) return true;
    const effectiveSupervisors = getContractFieldWithNews('supervisors') || [];
    return user.permissions.includes(PERMISSIONS.SUPERVISOR_CONTRACT.APPROVE)
      && effectiveSupervisors.some((s) => s?.userId === user.userId);
  }, [user, getContractFieldWithNews]);

  // El Apoyo a la Supervisión asignado marca su revisión o devuelve.
  const isSupportReviewer = user.permissions.includes(PERMISSIONS.SUPPORT_SUPERVISION.REVIEW)
    && supportIDs.includes(user.userId);
  const canReturnSocial = isSupervisorReviewer
    || (user.permissions.includes(PERMISSIONS.SUPPORT_SUPERVISION.RETURN)
      && supportIDs.includes(user.userId));

  // La revisión/aprobación exige la planilla (form): es el único comprobante
  // obligatorio; payroll/transaction/pensioner son anexos opcionales y no se
  // exigen en ninguna validación.
  const hasDocument = !!certificates?.form;

  const paymentPlan = getContractFieldWithNews('paymentPlan');

  const selectedIdx = payment ? Number(payment.split('_')[1]) : null;

  // Estado de revisión de la SS del mes seleccionado (fuente: contrato).
  // `Revisada` es el estado terminal; su presentación es independiente del
  // envío de la cuenta de cobro del mes (solo cuenta el propio componente).
  const ssReview = payment ? contract.payments?.[payment]?.socialSecurityReview : null;
  const ssStatus = getSocialSecurityStatus(payment ? contract.payments?.[payment] : null);
  const ssDisplayStatus = getSocialSecurityDisplayStatus(payment ? contract.payments?.[payment] : null);
  const ssReviewed = ssStatus === REVIEW_STATUS.REVIEWED;
  // Bloqueo por cuenta de cobro aprobada que usa esta SS (la del periodo
  // siguiente o, con pago anticipado, la del propio mes): nadie, ADMIN
  // incluido, deshace la revisión, devuelve ni edita. Se levanta devolviendo
  // esa cuenta de cobro desde el plan de pagos.
  const ssLockIdx = selectedIdx !== null
    ? getSocialSecurityLockIdx(contract, selectedIdx, getContractFieldWithNews)
    : -1;
  const ssLocked = ssLockIdx >= 0;
  const ssEditable = (isContractor || user.roles.includes('ADMIN')) && !ssReviewed && !ssLocked;

  // Campos marcados en la devolución vigente aún iguales a su snapshot, según
  // los valores ACTUALES del formulario: alimenta los iconos de advertencia
  // por campo (tiempo real, antes de guardar) y el gate de confirmación del
  // guardado. null = sin devolución vigente con snapshot (legado, sin iconos).
  const pendingCorrectionFields = isContractor && ssStatus === REVIEW_STATUS.RETURNED
    ? getSSPendingCorrectionFields(
      payment ? contract.payments?.[payment] : null,
      { contribution, payroll, payAt, certificates }
    )
    : null;

  // Icono de advertencia de un campo señalado en la devolución vigente y aún
  // sin corregir.
  const renderFieldWarning = (field) => (
    Array.isArray(pendingCorrectionFields) && pendingCorrectionFields.includes(field)
      ? (
        <Tooltip title={`Campo señalado en la devolución (${SS_RETURN_FIELDS[field]}): modifícalo para completar la corrección`}>
          <WarningAmberIcon color="warning" fontSize="small" sx={{ ml: 1 }} />
        </Tooltip>
      )
      : null
  );

  // Marcas de revisión sobre la SS del mes (Supervisor o Apoyo; una basta).
  const ssReviewMarks = Object.entries(ssReview?.reviews || {});
  const mySSReview = ssReview?.reviews?.[user.userId];
  const canSeeSSReviewers = isSupervisorReviewer || isSupportReviewer
    || user.roles.includes('ADMIN_CONTRACT');
  const supportName = (id) => {
    const support = (contract?.supports || []).find((s) => s?.userId === id);
    return support ? `${support.name} ${support.lastName}` : id;
  };

  // Fechas del periodo `idx` y, si se pasa `newsArr`, las novedades que lo
  // tocan. Fuente única compartida con la fila del plan de pagos: el rango y
  // los días trabajados salen de los días EJECUTABLES del plan vigente, así
  // que un mes recortado por una suspensión no se cuenta como mes completo
  // (de esos días trabajados depende el piso del IBC del aporte mínimo).
  const getPeriodInfo = (idx, newsArr) => {
    const info = getPeriodDateInfo(contract, getContractFieldWithNews('paymentPlanDays'), idx);

    if (newsArr) {
      collectPeriodNews(
        contract,
        getContractFieldWithNews('paymentPlan'),
        getContractFieldWithNews('finishedAt'),
        idx,
        newsArr
      );
    }

    return info;
  };

  // Aporte mínimo estimado del mes: IBC = 40% del honorario con piso del
  // salario mínimo prorrateado por días trabajados (base 30), por las tarifas
  // del independiente (salud 12.5% + pensión 16% + ARL del contrato). Es un
  // estimado informativo: nunca bloquea el guardado ni la revisión.
  //
  // Los días trabajados son los EJECUTABLES del periodo, no los del mes
  // calendario: un mes suspendido desde el día 20 aporta 19 días, y
  // prorratear sobre 30 inflaba el piso del IBC y encendía la alerta de
  // aporte insuficiente sobre registros que sí cumplían.
  const getMinContribution = (idx) => {
    const pay = paymentPlan?.[idx];
    const { salarioMinimo } = getPeriodTaxValues(contract?.startedAt, idx);
    if (!pay || !salarioMinimo) return null;
    const periodInfo = getPeriodInfo(idx);
    if (!periodInfo) return null;
    const { workingDays } = periodInfo;
    const arlRate = parseFloat(ARL[contract?.arlLevel]?.value || 0);
    const ibc = Math.max(pay * 0.4, salarioMinimo * (workingDays / 30));
    return { value: Math.round(ibc * (0.125 + 0.16 + arlRate)), workingDays };
  };

  // Correo de notificación de corrección de la SS al Supervisor efectivo y a
  // todos los Apoyos asignados con correo (mismo patrón multi-destinatario de
  // la corrección de actividades). Detalla cada campo marcado con su valor
  // devuelto (snapshot) y el corregido, y la observación de la devolución.
  const buildCorrectionEmail = (saved) => {
    const activeReturn = getActiveSSReturn(saved);
    if (!activeReturn?.snapshot) return undefined;

    const formatValue = (field, value) => {
      if (value === null || value === undefined || value === '') return '—';
      if (field === 'contribution') return currency(value, { precision: 0 }).format();
      if (field === 'payAt') return moment(value).format('YYYY-MM-DD');
      return String(value);
    };
    const items = (activeReturn.fields || []).map((field) => (
      field === 'certificates'
        ? {
          field: SS_RETURN_FIELDS.certificates,
          before: 'Comprobantes anteriores',
          after: 'Comprobantes actualizados',
        }
        : {
          field: SS_RETURN_FIELDS[field] || field,
          before: formatValue(field, activeReturn.snapshot[field]),
          after: formatValue(field, saved[field]),
        }
    ));

    const activeSupervisor = (getContractFieldWithNews('supervisors') || [])[0];
    const recipients = [
      activeSupervisor,
      ...(contract.supports || []).filter((s) => supportIDs.includes(s?.userId)),
    ].filter((r) => r?.email);
    if (recipients.length === 0) return undefined;

    const rangeInfo = getPeriodInfo(selectedIdx);
    const contractorName = `${contract.contractor.name} ${contract.contractor.lastName}`;

    return recipients.map((recipient) => ({
      emailTo: recipient.email,
      template: 'ssCorrectionNotification',
      data: {
        contractCode: contract.contractCode,
        usertitle: recipient.usertitle,
        fullname: `${recipient.name} ${recipient.lastName}`,
        position: recipient.position,
        entity: recipient.entity,
        dependence: recipient.dependence,
        contractorName,
        rangeDate: rangeInfo?.month,
        note: activeReturn.note || '',
        items,
      },
    }));
  };

  const handleUploadFile = async (evt, key) => {
    if (evt.target.files.length === 0) return;

    const file = evt.target.files[0];
    const encryptedError = await assertPdfNotEncrypted(file);
    if (encryptedError) {
      evt.target.value = '';
      setInfoSnack({ open: true, message: encryptedError, severity: 'error', duration: 5000 });
      return;
    }

    // ¿Esta carga completaría la corrección de una SS devuelta? Confirmar
    // ANTES de subir: la carga dispararía la notificación de corrección y
    // cancelar no debe persistir nada. La evaluación usa los valores
    // GUARDADOS del registro (la carga no persiste los campos del formulario)
    // con el comprobante nuevo simulado.
    if (isContractor && ssStatus === REVIEW_STATUS.RETURNED) {
      const stored = contract.payments?.[payment] || {};
      const candidate = {
        ...stored,
        certificates: { ...(stored.certificates || {}), [key]: `__nuevo_${key}__` },
      };
      const pending = getSSPendingCorrectionFields(stored, candidate);
      if (pending !== null && pending.length === 0) {
        evt.target.value = '';
        setCorrectionInfo({
          open: true,
          title: 'Enviar notificación de corrección',
          confirm: 'Cargar y notificar',
          data: { source: 'upload', key, file },
        });
        return;
      }
    }

    await uploadCertificate(file, key);
  };

  // Autollenado asistido desde el OCR de la planilla: extrae valor de los
  // aportes, número de planilla y fecha de pago, y llena SOLO los campos
  // vacíos del formulario, sin persistir (la persistencia sigue siendo del
  // botón "Actualizar"). Nunca bloquea la carga del comprobante: cualquier
  // fallo (OCR, OpenAI, parseo) deja los campos para diligenciamiento manual.
  // `quiet` evita pisar un snackbar más importante del flujo de carga
  // (p. ej. la notificación de corrección de una SS devuelta).
  const autofillFromPlanilla = async (response, quiet) => {
    const documentText = getUploadDocumentText(response);
    let data = null;

    if (documentText) {
      setAnalyzing(true);
      try {
        const messages = [
          { role: 'user', content: 'De acuerdo al siguiente texto de una planilla PILA de pago de aportes de seguridad social:' },
          { role: 'user', content: `""" ${documentText} """` },
          { role: 'user', content: 'Extrae la siguiente información solicitada, en formato JSON, usando las palabras entre paréntesis como keys del objeto JSON: """Valor total pagado de la planilla (contribution), Número de la planilla (payroll), Fecha de pago de la planilla (payAt)""". Escribe un formato JSON sin ningún comentario adicional ni saltos de página, que pueda ser parseado a objeto Javascript. El campo contribution escríbelo en formato numérico, sin puntos, ni comas ni caracteres especiales. El campo payroll escríbelo como texto compuesto únicamente por dígitos. El campo payAt escríbelo en formato YYYY-MM-DD. Si alguno de los datos no se identifica con certeza en el texto, escribe null en esa key. Ejemplo de salida esperada: {"contribution":285600,"payroll":"9045871234","payAt":"2025-04-08"}' },
        ];
        const aiResponse = await completionsCreate({ messages });
        data = JSON.parse(aiResponse.choices[0].message.content);
      } catch (err) {
        debugLog('Error extrayendo datos de la planilla:', err);
        data = null;
      }
      setAnalyzing(false);
    }

    const extractedContribution = Number(data?.contribution) > 0 ? Number(data.contribution) : null;
    const extractedPayroll = data?.payroll ? String(data.payroll) : null;
    const extractedPayAt = data?.payAt && moment(data.payAt, 'YYYY-MM-DD').isValid()
      ? moment(data.payAt, 'YYYY-MM-DD')
      : null;

    // Solo se llenan los campos vacíos: lo ya diligenciado se respeta.
    const filled = [];
    if (!contribution && extractedContribution) {
      setContribution(extractedContribution);
      filled.push('contribution');
    }
    if (!payroll && extractedPayroll) {
      setPayroll(extractedPayroll);
      filled.push('payroll');
    }
    if (!payAt && extractedPayAt) {
      setPayAt(extractedPayAt);
      filled.push('payAt');
    }

    if (quiet) return;
    if (filled.length > 0) {
      setInfoSnack({
        open: true,
        message: 'Datos extraídos automáticamente de la planilla: verifíquelos antes de actualizar.',
        severity: 'info',
        duration: 6000,
      });
    } else if (!extractedContribution && !extractedPayroll && !extractedPayAt) {
      setInfoSnack({
        open: true,
        message: 'No fue posible extraer los datos de la planilla; diligencie los campos manualmente.',
        severity: 'warning',
        duration: 6000,
      });
    }
  };

  const uploadCertificate = async (file, key) => {
    setLoading(true);
    const now = new Date().getTime();

    // Solo la planilla (form) pide OCR (Document AI): es el único comprobante
    // del que se autollenan los campos del registro.
    const isForm = key === 'form';
    const response = await UploadFile(
      user,
      file,
      process.env.REACT_APP_STORAGE_BUCKET_CONTRACTS,
      `${contract.contractCode}/certificate_${payment}_${key}_${now}.pdf`,
      isForm
    );

    if (response?.result) {
      let payments = {};
      if (contract.payments) payments = {...contract.payments};
      if (!payments[payment]) payments[payment] = { certificates };
      if (!payments[payment].certificates) payments[payment].certificates = certificates;
      payments[payment].certificates[key] = response.downloadUrl;

      // Corrección de una SS devuelta por carga de comprobante: con snapshot,
      // solo vuelve a Pendiente (invalidando las marcas de revisión) cuando
      // TODOS los campos marcados quedan corregidos, y en ese momento se
      // notifica la corrección; sin snapshot (devolución legada), reset con
      // cualquier carga y sin correo. El historial se conserva siempre.
      let email;
      if (isContractor && getSocialSecurityStatus(payments[payment]) === REVIEW_STATUS.RETURNED) {
        const pending = getSSPendingCorrectionFields(payments[payment], payments[payment]);
        if (pending === null || pending.length === 0) {
          if (pending !== null) email = buildCorrectionEmail(payments[payment]);
          payments[payment].socialSecurityReview = {
            ...(payments[payment].socialSecurityReview || {}),
            status: REVIEW_STATUS.PENDING,
            reviews: {},
          };
        }
      }

      await updateContract(contract.contractCode, { payments, email }, user);
      setContract({ ...contract, payments });
      setCertificates({ ...certificates, [key]: response.downloadUrl });

      if (email) {
        setInfoSnack({
          open: true,
          message: 'Corrección guardada y notificada al Supervisor para su re-revisión.',
          severity: 'success',
          duration: 6000,
        });
      }

      // Autollenado asistido desde la planilla recién cargada. En modo quiet
      // cuando hubo notificación de corrección: ese snackbar tiene prioridad.
      if (isForm && ssEditable) await autofillFromPlanilla(response, !!email);
    }

    setLoading(false);
  };

  const handleSubmit = async (evt) => {
    evt.preventDefault();

    // Corrección completa de una SS devuelta: confirmar ANTES de guardar,
    // porque el guardado dispara la notificación de corrección al Supervisor
    // (y Apoyos). Cancelar no persiste nada.
    if (Array.isArray(pendingCorrectionFields) && pendingCorrectionFields.length === 0) {
      setCorrectionInfo({
        open: true,
        title: 'Enviar notificación de corrección',
        confirm: 'Guardar y notificar',
        data: { source: 'submit' },
      });
      return;
    }

    await saveSocialSecurity();
  };

  const saveSocialSecurity = async () => {
    setLoading(true);
    let payments = {};
    if (contract.payments) payments = {...contract.payments};
    if (!payments[payment]) payments[payment] = {};
    payments[payment].contribution = contribution;
    payments[payment].payroll = payroll;
    payments[payment].payAt = payAt;
    payments[payment].certificates = certificates;

    // Corrección de una SS devuelta: con snapshot, la SS solo vuelve a
    // Pendiente (invalidando las marcas de revisión) cuando TODOS los campos
    // marcados difieren del snapshot, y en ese momento se notifica la
    // corrección a la revisión; los guardados parciales conservan Devuelta.
    // Devoluciones legadas sin snapshot: reset con cualquier edición, sin
    // correo. El historial de devoluciones se conserva siempre.
    let email;
    let pendingAfterSave = null;
    if (isContractor && getSocialSecurityStatus(payments[payment]) === REVIEW_STATUS.RETURNED) {
      pendingAfterSave = getSSPendingCorrectionFields(payments[payment], payments[payment]);
      if (pendingAfterSave === null || pendingAfterSave.length === 0) {
        if (pendingAfterSave !== null) email = buildCorrectionEmail(payments[payment]);
        payments[payment].socialSecurityReview = {
          ...(payments[payment].socialSecurityReview || {}),
          status: REVIEW_STATUS.PENDING,
          reviews: {},
        };
      }
    }

    await updateContract(contract.contractCode, { payments, email }, user);
    setContract({ ...contract, payments });
    setLoading(false);

    const minInfo = getMinContribution(selectedIdx);
    if (pendingAfterSave?.length > 0) {
      // Avance guardado sin completar la corrección: informa lo pendiente.
      setInfoSnack({
        open: true,
        message: `Avance guardado. La seguridad social permanece Devuelta: falta corregir ${pendingAfterSave.map((f) => SS_RETURN_FIELDS[f] || f).join(', ')}.`,
        severity: 'warning',
        duration: 6000,
      });
    } else if (email) {
      setInfoSnack({
        open: true,
        message: 'Corrección guardada y notificada al Supervisor para su re-revisión.',
        severity: 'success',
        duration: 6000,
      });
    } else if (!certificates?.form) {
      // Advertencia no bloqueante: se puede guardar el avance sin la planilla,
      // pero es el comprobante obligatorio para la revisión del mes.
      setInfoSnack({
        open: true,
        message: 'Registro guardado sin la planilla de seguridad social. Recuerde cargarla: es el comprobante obligatorio.',
        severity: 'warning',
        duration: 6000,
      });
    } else if (minInfo && Number(contribution) > 0 && Number(contribution) < minInfo.value) {
      // Aporte por debajo del mínimo estimado del período: informa sin bloquear.
      setInfoSnack({
        open: true,
        message: `Registro guardado: el valor de aportes es inferior al mínimo estimado del período (${currency(minInfo.value).format()} para ${minInfo.workingDays} días trabajados).`,
        severity: 'warning',
        duration: 6000,
      });
    }

    setPayment(undefined);
  };

  // Confirmación previa a la acción que completa la corrección de la SS
  // devuelta: guardar (submit) o cargar el comprobante pendiente. Cancelar no
  // persiste nada y la SS permanece Devuelta.
  const handleCorrectionConfirm = async (result, data) => {
    setCorrectionInfo({ open: false });
    if (!result) return;
    if (data?.source === 'upload' && data.file && data.key) {
      await uploadCertificate(data.file, data.key);
    } else {
      await saveSocialSecurity();
    }
  };

  // Marca (o deshace) la revisión del Supervisor o del Apoyo sobre la
  // seguridad social del mes. Solo afecta la entrada propia en
  // `socialSecurityReview.reviews`; `Revisada` (estado terminal) se deriva en
  // lectura: una marca de cualquiera de los dos basta.
  const handleToggleSSReview = async () => {
    if (!payment) return;
    // Con la SS bloqueada solo se permite marcar, nunca deshacer la marca.
    if (ssLocked && contract.payments?.[payment]?.socialSecurityReview?.reviews?.[user.userId]) return;
    setLoading(true);

    const review = { ...(contract.payments?.[payment]?.socialSecurityReview || {}) };
    const reviews = { ...(review.reviews || {}) };
    const isMarking = !reviews[user.userId];
    if (reviews[user.userId]) {
      delete reviews[user.userId];
    } else {
      reviews[user.userId] = {
        name: `${user.name} ${user.lastName}`,
        at: new Date().toISOString(),
      };
    }
    review.reviews = reviews;

    const payments = { ...(contract.payments || {}) };
    payments[payment] = { ...(payments[payment] || {}), socialSecurityReview: review };

    // Al MARCAR (no al deshacer), avisa al Supervisor efectivo. No se envía si
    // quien marca es el propio Supervisor. El correo viaja en el mismo
    // updateContract; una falla del correo no bloquea la marca.
    const activeSupervisor = (getContractFieldWithNews('supervisors') || [])[0];
    const rangeInfo = getPeriodInfo(selectedIdx);
    const email = (isMarking && activeSupervisor?.email && activeSupervisor.userId !== user.userId)
      ? {
        emailTo: activeSupervisor.email,
        template: 'componentReviewedSupervisorNotice',
        data: {
          contractCode: contract.contractCode,
          usertitle: activeSupervisor.usertitle,
          fullname: `${activeSupervisor.name} ${activeSupervisor.lastName}`,
          position: activeSupervisor.position,
          entity: activeSupervisor.entity,
          dependence: activeSupervisor.dependence,
          componentLabel: 'la seguridad social',
          reviewedBy: `${user.name} ${user.lastName}`,
          rangeDate: rangeInfo?.month,
        },
      }
      : undefined;

    await updateContract(contract.contractCode, {
      [`payments.${payment}.socialSecurityReview`]: review,
      email,
    }, user);

    setContract({ ...contract, payments });
    setLoading(false);
  };

  // Devolución de la seguridad social del mes (exige al menos un campo
  // inconsistente y una observación; notifica al contratista por correo) o su
  // reversa (`data.undo`): vuelve a Pendiente, marca la devolución vigente
  // como deshecha en el historial y notifica al contratista la anulación
  // (el correo de la devolución de SS es inmediato, hay que retractarlo).
  const handleSSReturn = async (result, data) => {
    if (!result || ssLocked) {
      setReturnInfo({ open: false });
      setReturnNote('');
      setReturnFields({});
      return;
    }

    if (data?.undo) {
      setReturnInfo({ open: false });
      setLoading(true);

      const at = new Date().toISOString();
      const name = `${user.name} ${user.lastName}`;
      const returns = [...(contract.payments?.[payment]?.socialSecurityReview?.returns || [])];
      let undoneReturn;
      for (let r = returns.length - 1; r >= 0; r -= 1) {
        if (!returns[r].undone) {
          undoneReturn = returns[r];
          returns[r] = { ...returns[r], undone: { userId: user.userId, name, at } };
          break;
        }
      }
      const review = {
        ...(contract.payments?.[payment]?.socialSecurityReview || {}),
        status: REVIEW_STATUS.PENDING,
        returns,
      };

      const rangeInfo = getPeriodInfo(selectedIdx);
      const email = contract.contractor?.email
        ? {
          emailTo: contract.contractor.email,
          template: 'undoDevolucionNotification',
          data: {
            contractCode: contract.contractCode,
            usertitle: contract.contractor.usertitle,
            fullname: `${contract.contractor.name} ${contract.contractor.lastName}`,
            position: contract.contractor.position,
            entity: contract.contractor.entity,
            dependence: contract.contractor.dependence,
            componentLabel: 'la seguridad social',
            undoneBy: name,
            returnedAt: undoneReturn ? moment(undoneReturn.at).format('YYYY-MM-DD') : undefined,
            note: undoneReturn?.note,
            rangeDate: rangeInfo?.month,
          },
        }
        : undefined;

      const payments = { ...(contract.payments || {}) };
      payments[payment] = { ...(payments[payment] || {}), socialSecurityReview: review };

      await updateContract(contract.contractCode, {
        [`payments.${payment}.socialSecurityReview`]: review,
        email,
      }, user);

      setContract({ ...contract, payments });
      setLoading(false);
      return;
    }

    const note = returnNote.trim();
    const fields = Object.keys(returnFields).filter((k) => returnFields[k]);
    if (fields.length === 0 || !note) {
      setInfoSnack({
        open: true,
        message: fields.length === 0
          ? 'Seleccione al menos un campo inconsistente'
          : 'La observación es requerida para devolver la seguridad social',
        severity: 'warning',
      });
      return;
    }

    setReturnInfo({ open: false });
    setReturnNote('');
    setReturnFields({});
    setLoading(true);

    const at = new Date().toISOString();
    const name = `${user.name} ${user.lastName}`;
    const role = contract?.supportIDs?.includes(user.userId) ? 'support' : 'supervisor';

    // Snapshot de los valores vigentes de los campos marcados: base de
    // comparación de la corrección del contratista (la SS solo vuelve a
    // Pendiente cuando todos difieran; ver getSSPendingCorrectionFields).
    const current = contract.payments?.[payment] || {};
    const snapshot = {};
    fields.forEach((field) => {
      if (field === 'certificates') {
        snapshot.certificates = {
          form: current.certificates?.form || null,
          payroll: current.certificates?.payroll || null,
          transaction: current.certificates?.transaction || null,
          pensioner: current.certificates?.pensioner || null,
        };
      } else if (field === 'payAt') {
        snapshot.payAt = current.payAt ? moment(current.payAt).format('YYYY-MM-DD') : null;
      } else {
        snapshot[field] = current[field] ?? null;
      }
    });

    const review = {
      ...(contract.payments?.[payment]?.socialSecurityReview || {}),
      status: REVIEW_STATUS.RETURNED,
      returns: [
        ...(contract.payments?.[payment]?.socialSecurityReview?.returns || []),
        { userId: user.userId, name, at, note, fields, role, snapshot },
      ],
    };

    const rangeInfo = getPeriodInfo(selectedIdx);
    const email = contract.contractor?.email
      ? {
        emailTo: contract.contractor.email,
        template: 'devolucionNotification',
        data: {
          contractCode: contract.contractCode,
          usertitle: contract.contractor.usertitle,
          fullname: `${contract.contractor.name} ${contract.contractor.lastName}`,
          position: contract.contractor.position,
          entity: contract.contractor.entity,
          dependence: contract.contractor.dependence,
          componentLabel: 'la seguridad social',
          note,
          fields: fields.map((f) => SS_RETURN_FIELDS[f]),
          returnedBy: name,
          rangeDate: rangeInfo?.month,
        },
      }
      : undefined;

    const payments = { ...(contract.payments || {}) };
    payments[payment] = { ...(payments[payment] || {}), socialSecurityReview: review };

    await updateContract(contract.contractCode, {
      [`payments.${payment}.socialSecurityReview`]: review,
      email,
    }, user);

    setContract({ ...contract, payments });
    setLoading(false);
  };

  // const handleCloseSocial = async (evt) => { // eslint-disable-line
  //   evt.preventDefault();
    
  //   let message;
  //   if (!contribution) message = 'Ingrese el valor de los aportes';
  //   if (!payroll) message = 'Ingrese el número de la planilla';
  //   if (!payAt) message = 'Ingrese la fecha de pago';
  //   if (!certificates.form) message = 'Debe cargar la planilla de pago de Seguridad social';
  //   if (!certificates.payroll) message = 'Debe cargar el comprobante de pago de la planilla';
  //   if (!certificates.transaction) message = 'Debe cargar el comprobante de la transacción';

  //   if (message) {
  //     setInfoSnack({ open: true, message, severity: 'warning' });
  //     return;
  //   }

  //   setLoading(true);
    
  //   let payments = {};
  //   if (contract.payments) payments = {...contract.payments};
  //   if (!payments[payment]) payments[payment] = {};
  //   payments[payment].contribution = contribution;
  //   payments[payment].payroll = payroll;
  //   payments[payment].payAt = payAt;
  //   payments[payment].certificates = certificates;
  //   payments[payment].socialApproved = false;

  //   await updateContract(contract.contractCode, { payments }, user);
  //   setContract({ ...contract, payments });
  //   setLoading(false);
  //   onClose();
  // };

  // Fila de un comprobante: botón de carga/descarga, marca de obligatoriedad
  // y ayuda que explica qué documento es y en qué se diferencia de los demás.
  const renderCertRow = (key, required = false) => (
    <Box key={key} sx={{ mb: 1.5 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
        {certificates[key] ? (
          <>
            <Link
              component="a"
              href={certificates[key]}
              target="_blank"
            >
              <Button
                component="label"
                role={undefined}
                variant="contained"
                startIcon={<DownloadIcon />}
                disabled={loading}
              >
                Ver Comprobante {certs[key]}
              </Button>
            </Link>
            {ssEditable && (
              <IconButton onClick={() => handleCertificateDelete(key)}>
                <DeleteIcon />
              </IconButton>
            )}
          </>
        ) : (
          <Button
            component="label"
            role={undefined}
            variant="outlined"
            startIcon={<CloudUploadIcon />}
            disabled={loading || !isContractor || ssReviewed || ssLocked}
          >
            {loading && <CircularProgress size={26} sx={{ mr: 1 }} />} Comprobante {certs[key]}
            <input
              hidden
              accept="*"
              type="file"
              onChange={(evt) => handleUploadFile(evt, key)}
            />
          </Button>
        )}
        {required && <Chip size="small" color="error" variant="outlined" label="Obligatorio" />}
      </Box>
      <FormHelperText sx={{ ml: 0.5, maxWidth: 420 }}>{CERT_HELP[key]}</FormHelperText>
    </Box>
  );

  const handleCertificateDelete = async (key) => {
    let payments = {};
    if (contract.payments) payments = {...contract.payments};
    if (!payments[payment]) payments[payment] = { certificates };
    if (!payments[payment].certificates) payments[payment].certificates = certificates;
    payments[payment].certificates[key] = null;

    setContract({ ...contract, payments });
    setCertificates({ ...certificates, [key]: null });
  };

  useEffect(() => {
    if (payment) {
      let pay = {};
      if (contract.payments && contract.payments[payment]) {
        pay = contract.payments[payment];
      }

      setContribution(pay.contribution);
      if (pay.payroll) setPayroll(pay.payroll);
      if (pay.payAt) setPayAt(pay.payAt);
      if (pay.certificates) setCertificates(pay.certificates);
    } else {
      setContribution(undefined);
      setPayroll('');
      setPayAt(null);
      setCertificates({ form: null,  payroll: null, transaction: null, pensioner: null });
    }
  }, [payment]); // eslint-disable-line

  // Permite abrir el modal directamente en un periodo (gating de la aprobación
  // de la cuenta de cobro). Si se abre desde el botón general, muestra la tabla.
  useEffect(() => {
    if (open) {
      setPayment(typeof initialPayment === 'string' ? initialPayment : undefined);
    }
  }, [open, initialPayment]); // eslint-disable-line

  useEffect(() => {
    if (!open) return;
    const el = modalRef.current;
    const id = requestAnimationFrame(updateScrollHint);
    window.addEventListener('resize', updateScrollHint);
    let observer;
    if (el && typeof ResizeObserver !== 'undefined') {
      observer = new ResizeObserver(updateScrollHint);
      observer.observe(el);
      Array.from(el.children).forEach((child) => observer.observe(child));
    }
    return () => {
      cancelAnimationFrame(id);
      window.removeEventListener('resize', updateScrollHint);
      if (observer) observer.disconnect();
    };
  }, [open, payment, contribution, payroll, payAt, certificates]); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <>
      <MySnackbar
        info={infoSnack}
        handleClose={() => setInfoSnack({ open: false })}
      />
      <DialogConfirmation
        confirmInfo={returnInfo}
        handleCloseDialog={handleSSReturn}
        content={returnInfo.data?.undo
          ? (
            <Typography variant="body2" sx={{ mt: 1, minWidth: 380 }}>
              La seguridad social del mes volverá al estado <strong>Pendiente</strong>.
              La devolución no se elimina del historial: quedará marcada como deshecha
              con tu nombre y la fecha.
            </Typography>
          )
          : (
          <Box sx={{ mt: 1, minWidth: 380 }}>
            <Typography variant="subtitle2" sx={{ mb: 1 }}>
              Campos que presentan inconsistencias
            </Typography>
            <FormGroup>
              {Object.keys(SS_RETURN_FIELDS).map((key) => (
                <FormControlLabel
                  key={key}
                  control={
                    <Checkbox
                      checked={!!returnFields[key]}
                      onChange={(evt) => setReturnFields({ ...returnFields, [key]: evt.target.checked })}
                    />
                  }
                  label={SS_RETURN_FIELDS[key]}
                />
              ))}
            </FormGroup>
            <TextField
              fullWidth
              multiline
              minRows={3}
              label="Observación (requerida)"
              placeholder="Describa el motivo de la devolución"
              value={returnNote}
              onChange={(evt) => setReturnNote(evt.target.value)}
              sx={{ mt: 2 }}
            />
          </Box>
          )
        }
      />
      <DialogConfirmation
        confirmInfo={correctionInfo}
        handleCloseDialog={handleCorrectionConfirm}
        content={(
          <Typography variant="body2" sx={{ mt: 1, minWidth: 380 }}>
            Todos los campos señalados en la devolución quedan corregidos. Al
            continuar, la seguridad social volverá al estado <strong>Pendiente</strong> y
            se enviará la notificación de corrección al Supervisor del contrato
            para su re-revisión.
          </Typography>
        )}
      />
      <Modal
        open={open}
        onClose={onClose}
      >
        <Box
          component="form"
          onSubmit={handleSubmit}
          sx={modalStyle}
          ref={modalRef}
          onScroll={updateScrollHint}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
            <Typography variant="h6" sx={{ flex: 1, fontWeight: 'bold' }}>Registro Seguridad Social</Typography>
            <CloseIcon sx={{ cursor: 'pointer' }} onClick={onClose} />
          </Box>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Registre los aportes de seguridad social de acuerdo con <strong>el mes al que corresponden</strong>. <br /> Ejemplo: si paga en <strong>abril</strong> los aportes de <strong>marzo</strong>, debe registrarlos en <strong>marzo</strong>.
          </Typography>
          {payment ? (
            <>
              {selectedIdx !== null && (() => {
                const selectedRange = getPeriodInfo(selectedIdx);
                if (!selectedRange) return null;
                return (
                  <Box
                    sx={{
                      mb: 3,
                      p: 2,
                      borderRadius: 1,
                      border: '1px solid',
                      borderColor: 'divider',
                      bgcolor: 'action.hover',
                    }}
                  >
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                      <Typography variant="overline" color="text.secondary">
                        Aportes del mes
                      </Typography>
                      <ReviewStatusChip status={ssDisplayStatus} />
                    </Box>
                    <Typography variant="h6" sx={{ fontWeight: 'bold', mt: 0.5 }}>
                      {selectedRange.month}
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                      Período: {selectedRange.range} · {selectedRange.workingDays} días trabajados
                    </Typography>
                    {/* La devolución es independiente del envío del mes y del
                        ciclo "En corrección" por actividades: exige solo la
                        planilla cargada. Desde `Revisada` sigue siendo
                        posible: es el camino de corrección del estado terminal. */}
                    {canReturnSocial && ssStatus !== REVIEW_STATUS.RETURNED && hasDocument && !ssLocked && (
                      <Button
                        variant="outlined"
                        color="error"
                        size="small"
                        startIcon={<AssignmentReturnIcon />}
                        sx={{ mt: 1.5 }}
                        disabled={loading}
                        onClick={() => setReturnInfo({
                          open: true,
                          title: `Devolver Seguridad Social — ${selectedRange.month}`,
                          confirm: 'Devolver',
                        })}
                      >
                        Devolver Seguridad Social
                      </Button>
                    )}
                    {/* Deshacer la devolución vigente de la SS: vuelve a Pendiente
                        conservando el historial. */}
                    {canReturnSocial && ssStatus === REVIEW_STATUS.RETURNED && !ssLocked && (
                      <Button
                        variant="outlined"
                        color="error"
                        size="small"
                        startIcon={<UndoIcon />}
                        sx={{ mt: 1.5 }}
                        disabled={loading}
                        onClick={() => setReturnInfo({
                          open: true,
                          title: `Deshacer devolución — ${selectedRange.month}`,
                          confirm: 'Deshacer',
                          data: { undo: true },
                        })}
                      >
                        Deshacer devolución
                      </Button>
                    )}
                    {/* Marcas de revisión (Supervisor o Apoyo) mientras el estado
                        no sea Revisada (p. ej. devuelta con marcas previas). Con
                        el estado Revisada, el Alert de abajo ya las lista. */}
                    {canSeeSSReviewers && ssReviewMarks.length > 0 && !ssReviewed && (
                      <Box sx={{ mt: 2, pt: 1.5, borderTop: '1px dashed', borderColor: 'divider' }}>
                        <Typography variant="subtitle2" sx={{ mb: 0.5 }}>
                          Marcas de revisión
                        </Typography>
                        {ssReviewMarks.map(([id, mark]) => (
                          <Typography key={id} variant="body2">
                            ✓ {mark?.name || supportName(id)}
                            {mark?.at
                              ? ` — ${moment(mark.at).format('YYYY-MM-DD HH:mm')}`
                              : ''}
                          </Typography>
                        ))}
                      </Box>
                    )}
                    {ssReview?.returns?.length > 0 && (
                      <Box sx={{ mt: 2, pt: 1.5, borderTop: '1px dashed', borderColor: 'divider' }}>
                        <Typography variant="subtitle2" sx={{ mb: 0.5 }}>
                          Historial de devoluciones ({ssReview.returns.length})
                        </Typography>
                        {/* Las devoluciones deshechas se conservan, atenuadas y con
                            la marca de quién y cuándo las deshizo. */}
                        {ssReview.returns.map((ret, rIdx) => (
                          <Box key={rIdx} sx={{ mb: 1, opacity: ret.undone ? 0.6 : 1 }}>
                            <Typography
                              variant="body2"
                              sx={{ fontWeight: 600, textDecoration: ret.undone ? 'line-through' : 'none' }}
                            >
                              {ret.name} — {moment(ret.at).format('YYYY-MM-DD HH:mm')}
                            </Typography>
                            {ret.fields?.length > 0 && (
                              <Typography
                                variant="caption"
                                color="text.secondary"
                                sx={{ textDecoration: ret.undone ? 'line-through' : 'none' }}
                              >
                                Campos: {ret.fields.map((f) => SS_RETURN_FIELDS[f] || f).join(', ')}
                              </Typography>
                            )}
                            <Typography variant="body2" sx={{ textDecoration: ret.undone ? 'line-through' : 'none' }}>
                              {ret.note}
                            </Typography>
                            {ret.undone && (
                              <Typography variant="caption" sx={{ fontStyle: 'italic' }}>
                                Deshecha por {ret.undone.name} — {moment(ret.undone.at).format('YYYY-MM-DD HH:mm')}
                              </Typography>
                            )}
                          </Box>
                        ))}
                      </Box>
                    )}
                  </Box>
                );
              })()}
            {/* Estado terminal Revisada: lista las marcas (Supervisor o Apoyo);
                para registros legados sin marcas cae a socialApprovedBy/At. */}
            {ssReviewed && (() => {
              const legacy = contract.payments?.[payment];
              const reviewedBy = ssReviewMarks.length > 0
                ? ssReviewMarks
                  .map(([id, mark]) => `${mark?.name || supportName(id)}${mark?.at ? ` (${moment(mark.at).format('DD/MM/YYYY')})` : ''}`)
                  .join(', ')
                : legacy?.socialApprovedBy?.name
                  ? `${legacy.socialApprovedBy.name}${legacy.socialApprovedAt ? ` (${moment(legacy.socialApprovedAt).format('DD/MM/YYYY')})` : ''}`
                  : '';
              return (
                <Alert
                  severity="success"
                  icon={<VerifiedIcon fontSize="inherit" />}
                  sx={{ mb: 2 }}
                >
                  Seguridad social revisada{reviewedBy ? ` por ${reviewedBy}` : ''}
                </Alert>
              );
            })()}
            {ssLocked && (
              <Alert severity="info" icon={<LockIcon fontSize="inherit" />} sx={{ mb: 2 }}>
                Bloqueada por la cuenta de cobro aprobada del Mes {getPeriodNumber(paymentPlan, ssLockIdx)}.
                Para corregirla, primero debe devolverse esa cuenta de cobro desde el plan de pagos.
              </Alert>
            )}
            {/* Alerta informativa (no bloquea): el aporte registrado quedó por
                debajo del mínimo estimado del período. */}
            {(() => {
              const minInfo = getMinContribution(selectedIdx);
              if (!minInfo || !(Number(contribution) > 0) || Number(contribution) >= minInfo.value) return null;
              return (
                <Alert severity="warning" icon={<WarningAmberIcon fontSize="inherit" />} sx={{ mb: 2 }}>
                  El valor de aportes registrado ({currency(Number(contribution)).format()}) es inferior
                  al mínimo estimado del período: {currency(minInfo.value).format()} para{' '}
                  {minInfo.workingDays} días trabajados (salud 12.5% + pensión 16% + ARL del contrato).
                  Verifique la planilla antes de continuar.
                </Alert>
              );
            })()}
            {/* Extracción en curso sobre la planilla recién cargada: el
                comprobante ya quedó guardado; esto solo autollena el formulario. */}
            {analyzing && (
              <Alert severity="info" icon={<CircularProgress size={18} />} sx={{ mb: 2 }}>
                Analizando la planilla para extraer el valor de los aportes, el número
                de planilla y la fecha de pago…
              </Alert>
            )}
            <Grid container spacing={2} sx={{ my: 3 }}>
              <Grid item xs={3} sx={{ display: 'flex', alignItems: 'center' }}>
                <FormControl fullWidth>
                  <MoneyInput
                    label="Valor de los aporte"
                    value={contribution}
                    onChange={(evt) => setContribution(evt.target.value)}
                    readOnly={!ssEditable}
                  />
                </FormControl>
                {renderFieldWarning('contribution')}
              </Grid>
              <Grid item xs={3} sx={{ display: 'flex', alignItems: 'center' }}>
                <FormControl fullWidth>
                  <TextField
                    label=" N° Planilla"
                    value={payroll}
                    onChange={(evt) => setPayroll(evt.target.value)}
                    InputProps={{ readOnly: !ssEditable }}
                  />
                </FormControl>
                {renderFieldWarning('payroll')}
              </Grid>
              <Grid item xs={3} sx={{ display: 'flex', alignItems: 'center' }}>
                <FormControl fullWidth>
                  <DatePicker
                    label="Fecha de pago"
                    value={payAt}
                    onChange={(val) => setPayAt(val)}
                    readOnly={!ssEditable}
                  />
                </FormControl>
                {renderFieldWarning('payAt')}
              </Grid>
              <Grid item xs={6} mt={1}>
                <Typography variant="subtitle2" sx={{ fontWeight: 'bold', mb: 1, display: 'flex', alignItems: 'center' }}>
                  Comprobantes{renderFieldWarning('certificates')}
                </Typography>
                {renderCertRow('form', true)}
                <Typography variant="subtitle2" color="text.secondary" sx={{ mt: 3, mb: 1 }}>
                  Anexos opcionales
                </Typography>
                {OPTIONAL_CERTS.map((key) => renderCertRow(key))}
              </Grid>
              <Grid item xs={12} sx={{ mt: 5 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <Button
                    type="button"
                    variant="outlined"
                    startIcon={<ArrowBackIcon />}
                    disabled={loading}
                    onClick={() => setPayment(undefined)}
                  >
                    {loading && <CircularProgress size={26} sx={{ mr: 1 }} />} Ver Tabla
                  </Button>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    {ssEditable && (
                      <Button
                        type="submit"
                        variant="contained"
                        disabled={loading}
                      >
                        {loading && <CircularProgress size={26} sx={{ mr: 1 }} />} Actualizar
                      </Button>
                    )}
                    {/* Supervisor y Apoyo comparten el mismo toggle de revisión:
                        `Revisada` es el estado terminal (no existe una aprobación
                        separada). Se habilita con la planilla cargada, sin
                        depender del envío de la cuenta de cobro del mes. */}
                    {(isSupportReviewer || isSupervisorReviewer)
                      && ssStatus !== REVIEW_STATUS.RETURNED
                      && !(ssLocked && mySSReview) && (
                      <Tooltip title={hasDocument || mySSReview
                        ? '' : 'Falta la planilla de seguridad social (comprobante obligatorio)'}
                      >
                        <span>
                          <Button
                            type="button"
                            variant={mySSReview ? 'outlined' : 'contained'}
                            color="success"
                            startIcon={<GradingIcon />}
                            disabled={loading || (!hasDocument && !mySSReview)}
                            onClick={handleToggleSSReview}
                          >
                            {loading && <CircularProgress size={26} sx={{ mr: 1 }} />}
                            {mySSReview ? 'Deshacer mi revisión' : 'Marcar revisada'}
                          </Button>
                        </span>
                      </Tooltip>
                    )}
                  </Box>
                </Box>
              </Grid>
            </Grid>
            </>
          ) : (
            <Grid container spacing={1} sx={{ my: 3 }}>
              <Grid item xs={12}>
                <Box sx={{ width: '100%' }}>
                  <TableContainer component={Paper}>
                    <Table sx={{ minWidth: 650 }} aria-label="plan de pagos">
                      <TableHead>
                        <TableRow>
                          <TableCell sx={{ fontWeight: 'bold' }}>Periodo de pago de los aportes sociales</TableCell>
                          <TableCell align="right" sx={{ fontWeight: 'bold' }}>Días Trabajados</TableCell>
                          <TableCell align="right" sx={{ fontWeight: 'bold' }}>Salario Base</TableCell>
                          <TableCell align="right" sx={{ fontWeight: 'bold' }}>Monto Planilla</TableCell>
                          <TableCell align="right" sx={{ fontWeight: 'bold' }}>Fecha de pago</TableCell>
                          <TableCell align="right" sx={{ fontWeight: 'bold' }}>Estado</TableCell>
                          <TableCell align="right" sx={{ fontWeight: 'bold' }}>Acciones</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {paymentPlan?.map((pay, idx) => {
                          // Un mes que una suspensión dejó en cero no es un
                          // periodo de pago: no exige aportes y no se lista.
                          // La clave `payment_${idx}` conserva el índice real.
                          if (!isExecutablePeriod(paymentPlan, idx)) return null;
                          const newsArr = [];
                          const rangeInfo = getPeriodInfo(idx, newsArr);
                          if (!rangeInfo || rangeInfo.startedAt.isAfter(moment(), 'month')) return null;
                          // IBC del período: piso del salario mínimo prorrateado
                          // por los días trabajados (base 30), que son los días
                          // EJECUTABLES del periodo (ver getPeriodDateInfo).
                          const { salarioMinimo } = getPeriodTaxValues(contract.startedAt, idx);
                          const baseSalary = Math.max(pay * 0.4, (salarioMinimo || 0) * (rangeInfo.workingDays / 30));
                          const minInfo = getMinContribution(idx);
                          
                          // const parent_approved = idx === 0 ? true : contract.payments && contract.payments[`payment_${idx - 1}`]?.approved;
                          const payment = contract.payments && contract.payments[`payment_${idx}`];

                          // Estado de la fila desde la presentación unificada de
                          // la SS, independiente del envío de la cuenta de cobro:
                          //  - rojo       => devuelta (pendiente de corrección del contratista)
                          //  - verde      => revisada (estado terminal)
                          //  - amarillo   => diligenciada (datos + planilla), pendiente de revisión
                          //  - gris claro => solo datos o solo comprobante (incompleto)
                          //  - sin color  => sin datos ni comprobante
                          const hasData = !!(payment && payment.contribution && payment.payroll && payment.payAt);
                          const hasDoc = !!payment?.certificates?.form;
                          const rowDisplayStatus = getSocialSecurityDisplayStatus(payment);

                          let rowBg = 'inherit';
                          let rowHoverBg = 'rgba(0, 0, 0, 0.04)';
                          let iconColor = 'action.disabled';
                          if (rowDisplayStatus === REVIEW_STATUS.RETURNED) {
                            rowBg = 'rgba(244, 67, 54, 0.12)';
                            rowHoverBg = 'rgba(244, 67, 54, 0.22)';
                            iconColor = 'error.main';
                          } else if (rowDisplayStatus === REVIEW_DISPLAY_SS_REVIEWED) {
                            rowBg = 'rgba(76, 175, 80, 0.12)';
                            rowHoverBg = 'rgba(76, 175, 80, 0.22)';
                            iconColor = 'success.main';
                          } else if (rowDisplayStatus === REVIEW_DISPLAY_SUBMITTED) {
                            rowBg = 'rgba(255, 193, 7, 0.18)';
                            rowHoverBg = 'rgba(255, 193, 7, 0.30)';
                            iconColor = 'warning.main';
                          } else if (hasData || hasDoc) {
                            rowBg = 'rgba(0, 0, 0, 0.06)';
                            rowHoverBg = 'rgba(0, 0, 0, 0.10)';
                            iconColor = 'action.active';
                          }

                          return (
                            <TableRow
                              key={idx}
                              sx={{
                                '&:last-child td, &:last-child th': { border: 0 },
                                backgroundColor: rowBg,
                                '&:hover': {
                                  backgroundColor: rowHoverBg
                                }
                              }}
                            >
                              <TableCell component="th" scope="row">
                                {rangeInfo.month}
                              </TableCell>
                              <TableCell align="right">
                                {rangeInfo.workingDays} días
                              </TableCell>
                              <TableCell align="right">
                                {currency(baseSalary).format()}
                              </TableCell>
                              <TableCell align="right">
                                {/* Aporte registrado por el contratista, no el
                                    honorario; resaltado si quedó bajo el mínimo. */}
                                {!payment?.contribution
                                  ? '--'
                                  : minInfo && payment.contribution < minInfo.value
                                    ? (
                                      <Tooltip title={`Inferior al aporte mínimo estimado del período: ${currency(minInfo.value).format()} para ${minInfo.workingDays} días trabajados`}>
                                        <Box component="span" sx={{ color: 'error.main', fontWeight: 600, display: 'inline-flex', alignItems: 'center', gap: 0.5 }}>
                                          <WarningAmberIcon sx={{ fontSize: 16 }} />
                                          {currency(payment.contribution).format()}
                                        </Box>
                                      </Tooltip>
                                    )
                                    : currency(payment.contribution).format()}
                              </TableCell>
                              <TableCell align="right">
                                {payment?.payAt ? moment(payment.payAt).format('YYYY-MM-DD') : '--'}
                              </TableCell>
                              <TableCell align="right">
                                <ReviewStatusChip status={rowDisplayStatus} />
                              </TableCell>
                              <TableCell align="right">
                                <IconButton size="small" onClick={() => setPayment(`payment_${idx}`)}>
                                  <HealthAndSafetyIcon
                                    sx={{ color: iconColor }}
                                  />
                                </IconButton>
                              </TableCell>
                            </TableRow>
                          );
                        })}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </Box>
              </Grid>
            </Grid>
          )}
          <Fade in={showScrollHint} unmountOnExit mountOnEnter>
            <Box
              sx={{
                position: 'sticky',
                bottom: 8,
                display: 'flex',
                justifyContent: 'center',
                pointerEvents: 'none',
                mt: -3,
                zIndex: 1,
              }}
            >
              <Tooltip title="Hay más contenido abajo">
                <IconButton
                  size="small"
                  onClick={() => modalRef.current?.scrollTo({ top: modalRef.current.scrollHeight, behavior: 'smooth' })}
                  sx={{
                    bgcolor: 'primary.main',
                    color: 'primary.contrastText',
                    boxShadow: 3,
                    pointerEvents: 'auto',
                    '&:hover': { bgcolor: 'primary.dark' },
                  }}
                >
                  <KeyboardArrowDownIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            </Box>
          </Fade>
        </Box>
      </Modal>
    </>
  );
};

export default SocialSecurityModal;
